/**
 * sensor.ts — camera-based habitat light & color sensing.
 *
 * Privacy contract (enforced here, promised by the consent banner):
 *  - one snapshot every captureIntervalMs, drawn to a single reused
 *    offscreen canvas (64×48 by default — far too small to identify
 *    anything);
 *  - the frame is BLURRED as it is drawn, then analyzed synchronously, then
 *    the canvas is cleared immediately — at most one blurred frame exists
 *    at any moment;
 *  - no frame is ever attached to the DOM, stored, or transmitted;
 *  - only derived numbers persist: a brightness scalar and a few hex colors.
 */
import { CONTRAST_LEVELS, contrast, deriveSemanticPalette, hex, hexToRgb, medianCut } from "./color.js";
import { CONSENT_KEY } from "./consent.js";
import type {
  AnalyzeResult,
  ColorScheme,
  ContrastLevel,
  DominantColor,
  HabitatSensorOptions,
  HabitatStateDetail,
  HabitatUpdateDetail,
  HabitatUpdateSource,
  PixelSource,
  SemanticPalette,
} from "./types.js";

export { CONSENT_KEY, contrast };

const STORAGE_KEY = "habitat:v1";
const CONTRAST_KEY = "habitat:contrast";
const SCHEME_KEY = "habitat:scheme";

interface PersistedState {
  brightness: number | null;
  palette: SemanticPalette | null;
  dominants: Array<{ hex: string; population: number }> | null;
  timestamp: number;
}

export class HabitatSensor extends EventTarget {
  readonly captureIntervalMs: number;
  readonly blurPx: number;
  readonly frameWidth: number;
  readonly frameHeight: number;

  private video: HTMLVideoElement | null = null;
  private stream: MediaStream | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  brightness: number | null = null; // EMA-smoothed 0–1
  palette: SemanticPalette | null = null;
  lastDominants: DominantColor[] | null = null; // kept so contrast changes re-derive in place
  sensing = false;
  level: ContrastLevel;
  /** Forced light/dark, or null for automatic (prefers-color-scheme /
      a .light /.dark class / whatever the room or clock says). */
  scheme: ColorScheme | null;

  constructor(options: HabitatSensorOptions = {}) {
    super();
    this.captureIntervalMs = options.captureIntervalMs ?? 15_000;
    this.blurPx = options.blurPx ?? 2;
    this.frameWidth = options.frameWidth ?? 64;
    this.frameHeight = options.frameHeight ?? 48;

    this.canvas = document.createElement("canvas"); // single reused buffer
    this.canvas.width = this.frameWidth;
    this.canvas.height = this.frameHeight;
    const ctx = this.canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("HabitatSensor: 2D canvas context unavailable");
    this.ctx = ctx;

    this.level = options.contrastLevel
      ?? (isContrastLevel(localStorage.getItem(CONTRAST_KEY)) ? (localStorage.getItem(CONTRAST_KEY) as ContrastLevel) : null)
      ?? (matchMedia("(prefers-contrast: more)").matches ? "high" : "aaa");
    document.documentElement.dataset.habitatContrast = this.level;

    // Unlike contrast, "unset" is a real, meaningful state here (automatic),
    // so there's no media-query fallback to coerce into — only apply the
    // data attribute (and thus win over prefers-color-scheme) when a scheme
    // is actually forced.
    this.scheme = options.colorScheme
      ?? (isColorScheme(localStorage.getItem(SCHEME_KEY)) ? (localStorage.getItem(SCHEME_KEY) as ColorScheme) : null);
    if (this.scheme) document.documentElement.dataset.habitatScheme = this.scheme;

    this.restore();
  }

  /* ----- persistence: derived numbers only, never pixels ----- */

  private restore(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const saved: PersistedState | null = raw ? JSON.parse(raw) : null;
      if (saved) {
        this.brightness = saved.brightness ?? null;
        this.palette = saved.palette ?? null;
        this.lastDominants = saved.dominants?.map((d) => ({
          rgb: hexToRgb(d.hex), population: d.population,
        })) ?? null;
        this.applyToRoot();
      }
    } catch { /* corrupt state — start fresh */ }
    if (this.brightness == null) this.applyFallback();
  }

  private persist(): void {
    const state: PersistedState = {
      brightness: this.brightness,
      palette: this.palette,
      dominants: this.lastDominants?.map((d) => ({
        hex: hex(d.rgb), population: +d.population.toFixed(4),
      })) ?? null,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  /* ----- lifecycle ----- */

  async start(): Promise<void> {
    if (this.sensing) return;
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 320 }, facingMode: "user" },
      audio: false,
    });
    this.video = document.createElement("video");
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.srcObject = this.stream; // never attached to the DOM
    await this.video.play();
    this.sensing = true;
    this.emit("habitatstate", { sensing: true });
    this.capture();
    this.timer = setInterval(() => this.capture(), this.captureIntervalMs);
  }

  stop(): void {
    if (this.timer != null) clearInterval(this.timer);
    this.timer = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.video = null;
    this.sensing = false;
    this.emit("habitatstate", { sensing: false });
  }

  /** Stop sensing and wipe everything persisted (contrast pref stays —
      it's an accessibility setting, not sensed data). */
  forget(): void {
    this.stop();
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CONSENT_KEY);
    this.brightness = null;
    this.palette = null;
    this.lastDominants = null;
    this.applyFallback();
  }

  /* ----- accessibility contrast ----- */

  /** Set the contrast target and re-derive the palette in place from the
      last dominant colors. */
  setContrastLevel(level: ContrastLevel): void {
    if (!CONTRAST_LEVELS[level]) return;
    this.level = level;
    localStorage.setItem(CONTRAST_KEY, level);
    document.documentElement.dataset.habitatContrast = level;
    if (this.lastDominants?.length) {
      this.palette = deriveSemanticPalette(this.lastDominants, CONTRAST_LEVELS[level], {
        colorScheme: this.scheme ?? undefined,
      });
      this.applyToRoot();
      this.persist();
      this.emit("habitatupdate", {
        brightness: this.brightness, palette: this.palette,
        dominants: this.lastDominants, source: "contrast",
      });
    }
  }

  /**
   * Force light or dark (or pass null to go back to automatic). Updates
   * `data-habitat-scheme` on <html> — which CSS uses to override both
   * off-mode's base palette and eink's paper — and re-derives the camo
   * palette in place from the last dominant colors, if any exist yet.
   */
  setColorScheme(scheme: ColorScheme | null): void {
    if (scheme != null && scheme !== "light" && scheme !== "dark") return;
    this.scheme = scheme;
    if (scheme) {
      localStorage.setItem(SCHEME_KEY, scheme);
      document.documentElement.dataset.habitatScheme = scheme;
    } else {
      localStorage.removeItem(SCHEME_KEY);
      delete document.documentElement.dataset.habitatScheme;
    }
    if (this.lastDominants?.length) {
      this.palette = deriveSemanticPalette(this.lastDominants, CONTRAST_LEVELS[this.level], {
        colorScheme: scheme ?? undefined,
      });
      this.applyToRoot();
      this.persist();
      this.emit("habitatupdate", {
        brightness: this.brightness, palette: this.palette,
        dominants: this.lastDominants, source: "scheme",
      });
    }
  }

  /* ----- capture → blur → analyze → clear ----- */

  private capture(): void {
    if (!this.video || this.video.readyState < 2) return;
    // Blur at the moment of drawing: no sharp frame ever exists, even
    // transiently (the frame downscale is itself a second blur).
    if (typeof this.ctx.filter === "string") this.ctx.filter = `blur(${this.blurPx}px)`;
    this.ctx.drawImage(this.video, 0, 0, this.frameWidth, this.frameHeight);
    this.ctx.filter = "none";
    let frame: ImageData | null = this.ctx.getImageData(0, 0, this.frameWidth, this.frameHeight);
    this.ctx.clearRect(0, 0, this.frameWidth, this.frameHeight); // canvas holds pixels no longer
    const result = this.analyze(frame);
    frame = null; // last reference to the pixels is gone
    this.ingest(result, "camera");
  }

  /**
   * Pure analysis of one pixel source (also callable from the console with
   * a synthetic frame for testing).
   */
  analyze(imageData: PixelSource): AnalyzeResult {
    const { data } = imageData;
    let lum = 0;
    for (let i = 0; i < data.length; i += 4) {
      lum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    }
    const brightness = lum / (data.length / 4) / 255;
    const dominants = medianCut(imageData, 8);
    return {
      brightness, dominants,
      palette: deriveSemanticPalette(dominants, CONTRAST_LEVELS[this.level], {
        colorScheme: this.scheme ?? undefined,
      }),
    };
  }

  private ingest({ brightness, dominants, palette }: AnalyzeResult, source: HabitatUpdateSource): void {
    // EMA so a hand passing the camera doesn't flash the page.
    this.brightness = this.brightness == null
      ? brightness
      : this.brightness * 0.6 + brightness * 0.4;
    this.palette = palette;
    this.lastDominants = dominants;
    this.applyToRoot();
    this.persist();
    this.emit("habitatupdate", {
      brightness: this.brightness, palette, dominants, source,
    });
  }

  /* ----- outputs ----- */

  private applyToRoot(): void {
    const root = document.documentElement.style;
    if (this.brightness != null) {
      root.setProperty("--habitat-brightness", this.brightness.toFixed(3));
    }
    if (this.palette) {
      // Written as --habitat-camo-*: habitat.css maps them to the semantic
      // palette only while camo mode is active, so inline styles can't
      // leak into eink/off modes.
      root.setProperty("--habitat-camo-bg", this.palette.bg);
      root.setProperty("--habitat-camo-surface", this.palette.surface);
      root.setProperty("--habitat-camo-text", this.palette.text);
      root.setProperty("--habitat-camo-text-muted", this.palette.textMuted);
      root.setProperty("--habitat-camo-accent", this.palette.accent);
      root.setProperty("--habitat-camo-border", this.palette.border);
      root.setProperty("--habitat-camo-shadow", this.palette.shadow);
    }
  }

  /** No camera (denied / unavailable): estimate brightness from local time. */
  applyFallback(): void {
    const h = new Date().getHours() + new Date().getMinutes() / 60;
    // Daylight curve peaking at 13:00; floor of 0.15 at night.
    let b = clampUnit(Math.cos(((h - 13) / 12) * Math.PI) * 0.55 + 0.45);
    if (matchMedia("(prefers-color-scheme: dark)").matches) b = Math.min(b, 0.45);
    this.brightness = b;
    this.applyToRoot();
    this.emit("habitatupdate", {
      brightness: b, palette: this.palette, dominants: null, source: "fallback",
    });
  }

  private emit(type: "habitatupdate", detail: HabitatUpdateDetail): void;
  private emit(type: "habitatstate", detail: HabitatStateDetail): void;
  private emit(type: string, detail: HabitatUpdateDetail | HabitatStateDetail): void {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }
}

function isContrastLevel(value: string | null): value is ContrastLevel {
  return value === "aa" || value === "aaa" || value === "high";
}

function isColorScheme(value: string | null): value is ColorScheme {
  return value === "light" || value === "dark";
}

function clampUnit(v: number): number {
  return Math.min(1, Math.max(0.15, v));
}
